/**
 * One row of a job list: type, status, "Dauer", a relative finish time, and
 * a truncated error with the full text on hover.
 *
 * REUSED BY TASK 13's Jobs view (task-8-brief.md: "<JobLine job> reused by
 * Task 13") -- the single required `job` prop and the absence of any
 * Start-specific state is deliberate so that reuse needs no rewrite, only an
 * import. `nowMs` is optional exactly so a caller rendering many rows in one
 * list (Task 13's monitor) can pass one shared "now" instead of each row
 * computing a slightly different one.
 *
 * Every decision this renders (which pill kind, what "Dauer" says, how the
 * timestamp reads, where the truncation cuts) lives in lib/jobDisplay.ts and
 * is asserted there in Node -- see that file's docblock for why.
 */
import { formatDuration, formatRelativeTime, jobStatusPill, truncateError } from '../lib/jobDisplay';
import { t } from '../lib/i18n';
import { StatusPill } from './StatusPill';
import type { PublicJob } from '../lib/types';

export function JobLine({ job, nowMs = Date.now() }: { job: PublicJob; nowMs?: number }) {
  const pill = jobStatusPill(job.status);
  const shortError = truncateError(job.error);
  return (
    <li className="job-line" data-job-type={job.type}>
      <span className="job-line-type">{job.type}</span>
      <StatusPill kind={pill.kind} label={pill.label} />
      <span className="job-line-duration">
        {t('jobLine.durationLabel')}: {formatDuration(job.durationMs)}
      </span>
      <span className="job-line-finished">{formatRelativeTime(job.finishedAt, nowMs)}</span>
      {shortError === null ? null : (
        <span className="job-line-error" title={job.error ?? undefined}>
          {shortError}
        </span>
      )}
    </li>
  );
}
